import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { RwButton } from '@/common/presentation/components/rw';

export interface PageHeaderProps {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  actionTestId?: string;
  icon?: ReactNode;
  children?: ReactNode;
}

export function PageHeader({ title, description, actionLabel, onAction, actionTestId, icon, children }: PageHeaderProps) {
  return (
    <Box sx={{ mb: 'var(--size-24)' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexDirection={{ xs: 'column', md: 'row' }}
        sx={{ gap: { xs: 'var(--size-16)', md: 'var(--size-8)' }, width: '100%', maxWidth: '100%', minWidth: 0 }}
      >
        <Box display="flex" flexDirection="column" sx={{ gap: 'var(--size-8)', minWidth: 0, flex: '1 1 auto' }}>
          <Box display="flex" alignItems="center" sx={{ gap: 'var(--size-8)', minWidth: 0 }}>
            {icon ? (
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', '& svg': { fontSize: 28 } }} aria-hidden>
                {icon}
              </Box>
            ) : null}
            <Typography
              variant="h3"
              component="h1"
              id="page-heading"
              tabIndex={-1}
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                // Host MUI themes pin h3 at 32px. The compact DS scale (≤1535px) is the token.
                fontSize: 'var(--font-h3-size)',
                lineHeight: 'var(--font-h3-line-height)',
                // Headings receive focus via the route-change hook; hide the default outline.
                outline: 'none'
              }}
            >
              {title}
            </Typography>
          </Box>
          {description ? (
            <Box display="flex" alignItems="center" sx={{ gap: 'var(--size-8)' }}>
              {icon ? <Box sx={{ width: 28, height: 28, flexShrink: 0 }} aria-hidden /> : null}
              <Typography variant="body2" sx={{ color: 'var(--text-body)', overflowWrap: 'anywhere', minWidth: 0 }}>
                {description}
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--size-12)',
            flexShrink: 0,
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            width: { xs: '100%', md: 'auto' },
            maxWidth: '100%'
          }}
        >
          {children}
          {actionLabel && onAction ? (
            <RwButton tier="primary" startIcon={<AddIcon aria-hidden />} onClick={onAction} data-testid={actionTestId}>
              {actionLabel}
            </RwButton>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
